// Tema claro/escuro do painel (WEB-100). Sem dependência nova: a preferência mora em
// `localStorage` (`leztypay.theme`) e o script inline de `index.html` já pintou a classe
// `.dark` antes do primeiro quadro, então este provider só mantém o estado em React e
// reaplica quando o usuário troca.

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  applyTheme,
  readThemePref,
  resolveTheme,
  type ThemePref,
  writeThemePref,
} from '../../lib/theme.ts';

interface ThemeContextValue {
  /** O que a pessoa escolheu (pode ser `system`). */
  pref: ThemePref;
  /** O que está na tela agora, com `system` já resolvido. */
  resolved: 'light' | 'dark';
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => readThemePref());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readThemePref()));

  useEffect(() => {
    const sync = (): void => {
      const next = resolveTheme(pref);
      setResolved(next);
      applyTheme(next);
    };
    sync();
    if (pref !== 'system') {
      return;
    }
    // Em "sistema", seguir o SO em tempo real (a pessoa troca no meio da sessão).
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', sync);
    return () => {
      query.removeEventListener('change', sync);
    };
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    writeThemePref(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ pref, resolved, setPref }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme precisa estar dentro de <ThemeProvider>');
  }
  return ctx;
}
